import { FileText, PanelLeftClose } from 'lucide-react';
import { useCallback, useMemo } from 'react';
import { NavLink, useSearchParams } from 'react-router-dom';
import { ROUTE_PATHS } from '../../app/router/route-paths';
import type { MeResponse } from '../../entities/me/me-contract.types';
import {
  groupedStaffDocsReleasePages,
  normalizeStaffDocsReleaseDocParam
} from '../../features/docs-release/docs-release-inventory';
import { isRouteAllowed } from '../../shared/access/access';
import { resolveFirstAllowedRoute } from '../../shared/access/route-resolver';
import { toFrontendStaffPath } from '../../shared/access/route-bridge';
import { STAFF_SPA_ROUTE_PATHS } from '../../shared/access/staff-spa-route-paths';
import { useAuth } from '../../shared/auth/use-auth';
import { cn } from '../../shared/lib/cn';
import { Sidebar, sidebarNavItemClassName } from '@domcrm/ui';
import styles from './staff-sidebar.module.css';

function resolveDocsSidebarExitHref(me: MeResponse | null | undefined): string {
  const cap = me?.capabilities;
  const primary = resolveFirstAllowedRoute(cap ?? null, me);
  if (primary !== ROUTE_PATHS.docs) return primary;
  for (const backendKey of STAFF_SPA_ROUTE_PATHS) {
    if (backendKey === '/docs' || backendKey === '/') continue;
    if (!isRouteAllowed(cap, backendKey)) continue;
    return toFrontendStaffPath(backendKey);
  }
  return isRouteAllowed(cap, '/me') ? ROUTE_PATHS.me : ROUTE_PATHS.forbidden;
}

export type StaffDocsSidebarNavProps = {
  narrowNav: boolean;
  onMobileClose?: () => void;
};

export function StaffDocsSidebarNav({ narrowNav, onMobileClose }: StaffDocsSidebarNavProps) {
  const { me } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const docId = useMemo(() => normalizeStaffDocsReleaseDocParam(searchParams.get('doc')), [searchParams]);
  const groups = useMemo(() => groupedStaffDocsReleasePages(), []);

  const exitHref = useMemo(() => resolveDocsSidebarExitHref(me), [me]);

  const setDoc = useCallback(
    (id: string) => {
      setSearchParams({ doc: id }, { replace: true });
      onMobileClose?.();
    },
    [setSearchParams, onMobileClose]
  );

  return (
    <Sidebar className={styles.sidebarNav}>
      <div className="flex flex-col gap-1 border-b border-[hsl(var(--ui-border)/0.55)] pb-2">
        <NavLink
          to={exitHref}
          end={false}
          className={({ isActive }) => cn(sidebarNavItemClassName(isActive), narrowNav && 'justify-center px-1.5')}
          aria-label={narrowNav ? 'К меню приложения' : undefined}
          title={narrowNav ? 'К меню приложения' : undefined}
          onClick={() => onMobileClose?.()}
        >
          <PanelLeftClose className="size-4 shrink-0 text-[hsl(var(--ui-muted-foreground))]" aria-hidden />
          {narrowNav ? null : <span className="min-w-0 flex-1 truncate">К меню приложения</span>}
        </NavLink>
      </div>
      {groups.map((group) => (
        <div key={group.id} className="flex flex-col gap-0.5">
          <p className="mb-1 mt-2 px-0.5 text-xs font-medium uppercase tracking-wide text-[hsl(var(--ui-muted-foreground))]">
            {group.title}
          </p>
          <ul
            className="flex flex-col gap-0.5"
            aria-label={`Документы: ${group.title}`}
            data-testid={`staff-docs-nav-${group.id}`}
          >
            {group.pages.map((p) => {
              const active = p.id === docId;
              return (
                <li key={p.id}>
                  <button
                    type="button"
                    data-testid={`staff-docs-nav-${p.id}`}
                    title={narrowNav ? p.title : undefined}
                    aria-current={active ? 'true' : undefined}
                    className={cn(
                      sidebarNavItemClassName(active),
                      narrowNav && 'justify-center px-1.5',
                      active && 'text-[hsl(var(--ui-foreground))]'
                    )}
                    onClick={() => setDoc(p.id)}
                  >
                    <FileText className="size-4 shrink-0 text-[hsl(var(--ui-muted-foreground))]" aria-hidden />
                    {narrowNav ? null : <span className="min-w-0 flex-1 truncate text-left">{p.title}</span>}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </Sidebar>
  );
}
